import React, { useEffect, useMemo, useState } from "react";
import SenderStep from "./SenderStep";
import TypeStep from "./TypeStep";
import DesignStep from "./DesignStep";
import ContentStep from "./ContentStep";
import RecipientsStep from "./RecipientsStep";
import ReviewStep from "./ReviewStep";
import ScheduleStep from "./ScheduleStep";
import WarningModal from "./WarningModal";

const API_URL = window.location.origin;

const STEPS = [
  "Sender",
  "Type",
  "Design",
  "Content",
  "Recipients",
  "Review & Confirm",
  "Schedule"
];

const INITIAL_FORM = {
  name: "",
  subject: "",
  previewText: "",
  fromName: "",
  fromEmail: "",
  replyToEmail: "",
  selectedSenders: [],
  editorType: "drag",
  template: "",
  html: "",
  recipientMode: "all",
  selectedContacts: [],
  selectedLists: [],
  selectedTags: [],
  excludedContacts: [],
  excludedLists: [],
  excludedTags: [],
  scheduleType: "now",
  scheduledDate: "",
  scheduledTime: "",
  timezone: "Asia/Karachi",
  sendRate: "balanced",
  sendRatePerMinute: 60,
  sendGapMinutes: 0,
  tracking: {
    googleAnalytics: false,
    openTracking: true,
    clickTracking: true,
    unsubscribeTracking: true
  }
};

function getCampaignErrorMessage(data, fallback = "Campaign submission failed.") {
  const rawMessage = data?.error || data?.message || fallback;

  if (String(rawMessage).toLowerCase().includes("no valid recipients")) {
    return "No valid recipients found. Please remove invalid contacts or add at least one valid email address, then try again.";
  }

  if (String(rawMessage).toLowerCase().includes("invalid recipient email")) {
    return "One or more recipient email addresses are invalid. Please clean your contacts list and try again.";
  }

  if (String(rawMessage).toLowerCase().includes("no smtp")) {
    return "No SMTP account is configured. Please add a working SMTP account before sending this campaign.";
  }

  if (String(rawMessage).toLowerCase().includes("authentication")) {
    return "SMTP authentication failed. Please check your SMTP username and password.";
  }

  if (String(rawMessage).toLowerCase().includes("failed")) {
    return rawMessage;
  }

  return rawMessage;
}

export default function CampaignBuilder({ onClose, initialCampaign = null }) {
  const [step, setStep] = useState(0);

  const [form, setForm] = useState(() => {
    if (!initialCampaign) return INITIAL_FORM;

    return {
      ...INITIAL_FORM,
      ...initialCampaign,
      status: "draft"
    };
  });

  const [senders, setSenders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [tags, setTags] = useState([]);

  const [warningOpen, setWarningOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [builderError, setBuilderError] = useState("");
  const [builderSuccess, setBuilderSuccess] = useState("");

  async function loadData() {
    try {
      const [senderRes, smtpRes, contactRes, listRes, tagRes] = await Promise.all([
        fetch(`${API_URL}/api/senders`),
        fetch(`${API_URL}/api/smtp`),
        fetch(`${API_URL}/api/contacts`),
        fetch(`${API_URL}/api/lists`),
        fetch(`${API_URL}/api/tags`)
      ]);

      const senderData = senderRes.ok ? await senderRes.json() : [];
      const smtpData = smtpRes.ok ? await smtpRes.json() : [];
      const contactData = contactRes.ok ? await contactRes.json() : [];
      const listData = listRes.ok ? await listRes.json() : [];
      const tagData = tagRes.ok ? await tagRes.json() : [];

      // Every SMTP the user adds should automatically appear as a selectable
      // sender in the campaign builder. Derive a sender from each SMTP account
      // and merge with saved senders, deduped by email (saved sender wins).
      const baseSenders = Array.isArray(senderData) ? senderData : [];
      const smtpSenders = (Array.isArray(smtpData) ? smtpData : [])
        .filter((s) => s && (s.senderEmail || s.fromEmail || s.user))
        .map((s) => ({
          _id: s._id,
          smtpId: s._id,
          name: s.senderName || s.name || "Sender",
          email: String(s.senderEmail || s.fromEmail || s.user || "").toLowerCase(),
          fromSmtp: true
        }));
      const mergedMap = new Map();
      [...baseSenders, ...smtpSenders].forEach((s) => {
        const email = String(
          s.email || s.senderEmail || s.fromEmail || ""
        ).toLowerCase();
        if (!email) return;
        if (!mergedMap.has(email)) mergedMap.set(email, { ...s, email });
      });
      const mergedSenders = Array.from(mergedMap.values());

      setSenders(mergedSenders);
      setContacts(Array.isArray(contactData) ? contactData : []);
      setLists(Array.isArray(listData) ? listData : []);
      setTags(Array.isArray(tagData) ? tagData : []);

      if (mergedSenders.length > 0) {
        setForm((prev) => ({
          ...prev,
          fromName: prev.fromName || mergedSenders[0].name || "Sender",
          fromEmail: prev.fromEmail || mergedSenders[0].email || "",
          selectedSenders:
            Array.isArray(prev.selectedSenders) && prev.selectedSenders.length
              ? prev.selectedSenders
              : [mergedSenders[0]]
        }));
      }
    } catch (error) {
      setBuilderError(
        "Unable to load campaign data. Please refresh the page and try again."
      );
      console.log("Campaign builder load error:", error.message);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function clearMessages() {
    setBuilderError("");
    setBuilderSuccess("");
  }

  function updateForm(key, value) {
    clearMessages();

    setForm((prev) => ({
      ...prev,
      [key]: value
    }));
  }

  function updateTracking(key, value) {
    clearMessages();

    setForm((prev) => ({
      ...prev,
      tracking: {
        ...prev.tracking,
        [key]: value
      }
    }));
  }

  function isBasicValidEmail(email = "") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim());
  }

  const activeContacts = useMemo(() => {
    return contacts.filter((contact) => {
      const status = contact.status || "active";

      return (
        contact.email &&
        isBasicValidEmail(contact.email) &&
        !contact.unsubscribed &&
        !contact.bounced &&
        (status === "active" || status === "subscribed")
      );
    });
  }, [contacts]);

  const recipientCount = useMemo(() => {
    if (form.recipientMode === "all") {
      return activeContacts.length;
    }

    const ids = new Set();

    activeContacts.forEach((contact) => {
      if (form.selectedContacts.includes(contact._id)) ids.add(contact._id);
      if (form.selectedLists.includes(contact.list)) ids.add(contact._id);
      if (form.selectedTags.includes(contact.tag)) ids.add(contact._id);
    });

    activeContacts.forEach((contact) => {
      if (form.excludedContacts.includes(contact._id)) ids.delete(contact._id);
      if (form.excludedLists.includes(contact.list)) ids.delete(contact._id);
      if (form.excludedTags.includes(contact.tag)) ids.delete(contact._id);
    });

    return ids.size;
  }, [activeContacts, form]);

  function canContinue() {
    clearMessages();

    if (step === 0) {
      if (!form.name.trim()) {
        setBuilderError("Please enter a campaign name.");
        return false;
      }

      if (!form.subject.trim()) {
        setBuilderError("Please enter an email subject.");
        return false;
      }

      const chosenSenders = Array.isArray(form.selectedSenders) && form.selectedSenders.length
        ? form.selectedSenders
        : form.fromEmail
        ? [{ name: form.fromName || "Sender", email: form.fromEmail }]
        : [];

      if (!chosenSenders.length) {
        setBuilderError("Please select at least one sender email.");
        return false;
      }

      const hasInvalidSender = chosenSenders.some((sender) => {
        const email = sender?.email || sender?.senderEmail || sender?.fromEmail || "";
        return !isBasicValidEmail(email);
      });

      if (hasInvalidSender) {
        setBuilderError("Please select valid sender email addresses.");
        return false;
      }

      return true;
    }

    if (step === 1) {
      if (!form.editorType) {
        setBuilderError("Please select an email type.");
        return false;
      }

      return true;
    }

    if (step === 2) {
      if (!form.template) {
        setBuilderError("Please select a template.");
        return false;
      }

      return true;
    }

    if (step === 3) {
      if (!form.html.trim()) {
        setBuilderError("Please add email content before continuing.");
        return false;
      }

      return true;
    }

    if (step === 4) {
      if (recipientCount <= 0) {
        setBuilderError(
          "No valid recipients selected. Please add valid contacts or update your recipient filters."
        );
        return false;
      }

      return true;
    }

    if (step === 6 && form.scheduleType === "later") {
      if (!form.scheduledDate || !form.scheduledTime) {
        setBuilderError("Please select a schedule date and time.");
        return false;
      }

      return true;
    }

    return true;
  }

  function goNext() {
    if (!canContinue()) {
      return;
    }

    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }

    setWarningOpen(true);
  }

  function goToStep(index) {
    clearMessages();

    if (index <= step) {
      setStep(index);
    }
  }

  async function submitCampaign(action = "send") {
    try {
      setSubmitting(true);
      setBuilderError("");
      setBuilderSuccess("");

      if (recipientCount <= 0 && action !== "draft") {
        setBuilderError(
          "No valid recipients found. Please add at least one valid contact before sending."
        );
        return;
      }

      const selectedSenders = Array.isArray(form.selectedSenders) && form.selectedSenders.length
        ? form.selectedSenders
        : [{ name: form.fromName || "Sender", email: form.fromEmail }];

      const payload = {
        ...form,
        selectedSenders,
        senders: selectedSenders,
        fromName: selectedSenders[0]?.name || selectedSenders[0]?.senderName || form.fromName,
        fromEmail: selectedSenders[0]?.email || selectedSenders[0]?.senderEmail || form.fromEmail,
        totalRecipients: recipientCount,
        action
      };

      const url =
        action === "draft"
          ? `${API_URL}/api/campaigns/save-draft`
          : `${API_URL}/api/campaigns/send-now`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        const message = getCampaignErrorMessage(
          data,
          action === "draft"
            ? "Unable to save draft."
            : "Unable to send campaign."
        );

        setBuilderError(message);
        setWarningOpen(false);
        return;
      }

      setBuilderSuccess(
        action === "draft"
          ? "Campaign saved as draft successfully."
          : "Campaign queued successfully. Emails will be sent in the background."
      );

      setWarningOpen(false);

      setTimeout(() => {
        // Signal a real send (not a draft save) so the caller can redirect
        // appropriately (e.g. back to the Dashboard when launched from there).
        onClose(action === "draft" ? undefined : { sent: true });
      }, 600);
    } catch (error) {
      setBuilderError(
        error?.message || "Something went wrong. Please try again."
      );
      setWarningOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="sendx-campaign-builder">
      <div className="sendx-builder-subnav">
        <h2>{STEPS[step]}</h2>

        <div className="sendx-step-nav">
          {STEPS.map((item, index) => (
            <button
              key={item}
              type="button"
              className={[
                index === step ? "active" : "",
                index < step ? "done" : "",
                index <= step ? "clickable" : ""
              ].join(" ")}
              onClick={() => goToStep(index)}
            >
              <span>{item}</span>
              {index < STEPS.length - 1 && <b>›</b>}
            </button>
          ))}
        </div>
      </div>

      {(builderError || builderSuccess) && (
        <div
          className={
            builderError
              ? "campaign-builder-alert error"
              : "campaign-builder-alert success"
          }
        >
          <strong>{builderError ? "Action required" : "Success"}</strong>
          <p>{builderError || builderSuccess}</p>
        </div>
      )}

      <main className="sendx-builder-content">
        {step === 0 && (
          <SenderStep
            form={form}
            updateForm={updateForm}
            senders={senders}
            setSenders={setSenders}
            reloadSenders={loadData}
          />
        )}

        {step === 1 && <TypeStep form={form} updateForm={updateForm} />}

        {step === 2 && <DesignStep form={form} updateForm={updateForm} />}

        {step === 3 && <ContentStep form={form} updateForm={updateForm} />}

        {step === 4 && (
          <RecipientsStep
            form={form}
            updateForm={updateForm}
            contacts={activeContacts}
            lists={lists}
            tags={tags}
            selectedCount={recipientCount}
          />
        )}

        {step === 5 && (
          <ReviewStep
            form={form}
            selectedCount={recipientCount}
            goToStep={setStep}
          />
        )}

        {step === 6 && (
          <ScheduleStep
            form={form}
            updateForm={updateForm}
            updateTracking={updateTracking}
          />
        )}
      </main>

      <footer className="sendx-builder-footer">
        <button
          type="button"
          className="sendx-dark-btn"
          disabled={submitting}
          onClick={() => {
            clearMessages();

            if (step === 0) {
              onClose();
            } else {
              setStep(step - 1);
            }
          }}
        >
          {step === 0 ? "Cancel" : "‹ Back"}
        </button>

        {step === STEPS.length - 1 && (
          <button
            type="button"
            className="sendx-dark-btn"
            onClick={() => submitCampaign("draft")}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save as Draft"}
          </button>
        )}

        <button
          type="button"
          className="sendx-yellow-btn"
          onClick={goNext}
          disabled={submitting}
        >
          {submitting
            ? "Submitting..."
            : step < STEPS.length - 1
            ? `Next: ${STEPS[step + 1]}`
            : form.scheduleType === "later"
            ? "Schedule"
            : "Send"}
        </button>
      </footer>

      {warningOpen && (
        <WarningModal
          form={form}
          selectedCount={recipientCount}
          submitting={submitting}
          onCancel={() => setWarningOpen(false)}
          onConfirm={() => submitCampaign("send")}
        />
      )}
    </section>
  );
}